import { useEffect, useState, useRef } from "react";
import type { ChangeEvent } from "react";
import { Upload } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDate, cn, formatPhoneDisplay, formatClinicPhone } from "@/lib/utils.ts";
import { Badge } from "@/components/ui/badge";
import MainLayout from "@/layouts/MainLayout.tsx";
import { toast } from "sonner";
import StatusCell from "@/components/custom/StatusCell.tsx";
import AuthService from "@/lib/auth";
import Papa from "papaparse";
import ENV from "@/lib/env";
import { apiPost as internalApiPost } from "@/lib/api";

// If @types/papaparse is not available, see src/types/papaparse.d.ts
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const Patients = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [list, setList] = useState<any[]>([]);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [noResult, setNoResult] = useState(false);
  // Filter state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [availableTests, setAvailableTests] = useState<any[]>([]);
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [updating, setUpdating] = useState(false);

  // Calculate age helper
  const calculateAge = (dob: string | null | undefined): number | null => {
    if (!dob) return null;
    const dobDate = new Date(dob);
    if (isNaN(dobDate.getTime())) return null;
    const diff = Date.now() - dobDate.getTime();
    if (diff < 0) return 0;
    const ageDate = new Date(diff); 
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const handleFilter = () => {
    // Implement filter logic here (date range & search)
    // For now, just refetch patients (add filter logic as needed)
    fetchPatients();
  };

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const data = await AuthService.getPatients();
      setList(data || []);
      setNoResult(!data?.length);
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const templates = await AuthService.getQuestionnaireTemplates({ is_active: true });
      setAvailableTests(templates || []);
    } catch (error) {
      console.error("Error fetching tests:", error);
    }
  };

  const toggleCheckbox = (id: string) => {
    setSelectedPatients((prev) =>
      prev.includes(id)
        ? prev.filter((pid) => pid !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedPatients([]);
    } else {
      const allIds = list.map((p) => p.id);
      setSelectedPatients(allIds);
    }
    setSelectAll(!selectAll);
  };

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      const results = await new Promise<any[]>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results: any) => {
            if (results.errors.length > 0) {
              reject(new Error('Error parsing CSV file'));
              return;
            }
            resolve(results.data);
          },
          error: (error: any) => {
            reject(error);
          }
        });
      });

      // Process and validate each row with only essential fields
      const patientsToImport = results.map((row, index) => {
        const rowNumber = index + 2; // +2 for header row and 0-based index

        // Validate required fields
        const requiredFields = ['first_name', 'last_name', 'mobile_number'];
        const missingFields = requiredFields.filter(field => !row[field]);

        if (missingFields.length > 0) {
          throw new Error(`Row ${rowNumber}: Missing required fields: ${missingFields.join(', ')}`);
        }

        // Only include these essential fields that we know exist
        return {
          id: uuidv4(),
          first_name: String(row.first_name || '').trim(),
          last_name: String(row.last_name || '').trim(),
          mobile_number: String(row.mobile_number || '').replace(/\D/g, ''),
          email: row.email ? String(row.email).trim().toLowerCase() : null,
          enrolled_for_monitorhealth_services: false
        };
      });

      // Insert in batches to avoid timeouts
      const BATCH_SIZE = 10;
      for (let i = 0; i < patientsToImport.length; i += BATCH_SIZE) {
        const batch = patientsToImport.slice(i, i + BATCH_SIZE);
        await AuthService.importPatients(batch);
      }

      toast.success(`Successfully imported ${patientsToImport.length} patients`);
      await fetchPatients();

    } catch (error) {
      console.error('Error importing patients:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to import patients');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSendMessage = async () => {
    if (selectedPatients.length === 0) return;

    try {
      const patients = list.filter((p) => selectedPatients.includes(p.id));
      const messages = [];

      for (const patient of patients) {
        let questionnaire;

        if (patient?.questionnaire_response_id && patient?.token) {
          questionnaire = patient;
        } else {
          questionnaire = await AuthService.submitQuestionnaireResponse({
            patient_id: patient.id,
            questionnaire_type: patient?.questionnaire_type_id?.length > 0
              ? patient.questionnaire_type_id
              : 'PHQ-9',
            total_score: 0,
            self_reported: true,
            status: 'pending'
          });
        }

        if (questionnaire) {
          messages.push({
            id: patient.id,
            name: `${patient.first_name}${patient.middle_name ? ' ' + patient.middle_name : ''} ${patient.last_name}`,
            mobile_number: patient.mobile_number,
            url: `${ENV.VITE_BASE_URL}/questionnaire/t/${questionnaire.token}`,
          });
        }
      }

      if (messages.length > 0) {
        let successCount = 0;
        for (const msg of messages) {
          try {
            const firstName = msg.name?.split(" ")[0] || "there";
            const clinicPhone = formatClinicPhone("(555) 000-0004");
            const messageBody = `Ally Psychiatry (Pelham) via MonitorHealth: Hi ${firstName}, your care team has sent you a health questionnaire to complete: ${msg.url}\nThis link expires in 48 hours. Questions? Call ${clinicPhone}. Text STOP to opt out.`;
            await internalApiPost('/send-sms', { to: msg.mobile_number, body: messageBody });
            successCount++;
          } catch (e) {
            console.error(`Failed to send SMS to ${msg.name}:`, e);
          }
        }
        if (successCount > 0) toast.success(`Messages sent to ${successCount} patients`);
        if (successCount < messages.length) toast.error(`Failed to send to ${messages.length - successCount} patients`);
        setSelectedPatients([]);
        await fetchPatients();
      }
    } catch (error) {
      console.error("Error sending messages:", error);
       toast.error("Failed to send messages. Please try again.");
    }
  };

  const handleBulkAssign = async () => {
    if (selectedPatients.length === 0 || selectedTests.length === 0) return;
    
    setUpdating(true);
    try {
      const batchSize = 10;
      for (let i = 0; i < selectedPatients.length; i += batchSize) {
        const batch = selectedPatients.slice(i, i + batchSize);
        await Promise.all(batch.map(id => 
          AuthService.updatePatient(id, {
            selected_tests: selectedTests,
            enrolled_for_monitorhealth_services: true
          })
        ));
      }
      
      toast.success(`Successfully assigned tests to ${selectedPatients.length} patients`);
      setIsBulkAssignOpen(false);
      setSelectedTests([]);
      setSelectedPatients([]);
      await fetchPatients();
    } catch (error) {
      console.error("Error in bulk assign:", error);
      toast.error("Failed to assign tests to some patients");
    } finally {
      setUpdating(false);
    }
  };

  const toggleTest = (testId: string) => {
    setSelectedTests(prev => 
      prev.includes(testId) 
        ? prev.filter(id => id !== testId) 
        : (prev.length < 2 ? [...prev, testId] : prev)
    );
  };

  return (
    <MainLayout>
      <div className="w-full mx-auto p-4 mt-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <input
              type="date"
              className="border rounded-md px-3 py-2 text-sm"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              placeholder="Start Date"
            />
            <span className="mx-1 text-gray-400">to</span>
            <input
              type="date"
              className="border rounded-md px-3 py-2 text-sm"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              placeholder="End Date"
            />
            <input
              type="text"
              className="border rounded-md px-3 py-2 text-sm w-full sm:w-64"
              placeholder="Search by name or mobile number"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Button onClick={handleFilter} variant="secondary" className="px-4">Filter</Button>
            <Button
              variant="outline"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-2" />
              {importing ? 'Importing...' : 'Import CSV'}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".csv"
              className="hidden"
              disabled={importing}
            />
            <Button onClick={() => navigate('/patients/create')} className="bg-[#1a7a4a] hover:bg-[#1a7a4a]/90 text-white">
              Add Patient
            </Button>
          </div>
        </div>

        <h1 className="text-2xl font-semibold">Patient Management</h1>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : noResult ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No patients found.</p>
            <Button onClick={() => navigate('/patients/create')}>
              Register New Patient
            </Button>
          </div>
        ) : (
          <>
            <div className="rounded-lg border shadow-sm overflow-x-auto">
              {/* Filtering logic */}
              {(() => {
                let filteredList = list;
                if (searchTerm.trim()) {
                  const term = searchTerm.trim().toLowerCase();
                  filteredList = filteredList.filter(
                    p =>
                      (p.first_name && p.first_name.toLowerCase().includes(term)) ||
                      (p.last_name && p.last_name.toLowerCase().includes(term)) ||
                      (p.mobile_number && p.mobile_number.includes(term))
                  );
                }
                if (startDate) {
                  filteredList = filteredList.filter(p =>
                    p.created_at && new Date(p.created_at) >= new Date(startDate)
                  );
                }
                if (endDate) {
                  filteredList = filteredList.filter(p =>
                    p.created_at && new Date(p.created_at) <= new Date(endDate + 'T23:59:59')
                  );
                }

                return (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectAll}
                            onCheckedChange={toggleSelectAll}
                            className="border-gray-300"
                          />
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Mobile No.</TableHead>
                        <TableHead>Registration Date</TableHead>
                        <TableHead>Enrolled</TableHead>
                        <TableHead>Questionnaire Type</TableHead>
                        <TableHead>Link Sent At</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredList
                        .filter(patient => patient.first_name || patient.last_name)
                        .map((patient) => (
                          <TableRow key={patient.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedPatients.includes(patient.id)}
                                onCheckedChange={() => toggleCheckbox(patient.id)}
                                className="border-gray-300"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Link
                                  to={`/patients/${patient.id}`}
                                  className="text-primary hover:underline hover:text-primary/80 transition-colors font-medium"
                                >
                                  {patient.first_name}{patient.middle_name ? ` ${patient.middle_name}` : ''} {patient.last_name}
                                </Link>
                                {(patient.mindwise_uuid || Boolean(patient.id)) && (
                                  <span 
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200 shrink-0"
                                    title="MindWise Ingested Patient (This Week)"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    MindWise Ingested
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{formatPhoneDisplay(patient.mobile_number)}</TableCell>
                            <TableCell>
                              {formatDate(patient.created_at, "do MMM yyyy 'at' h:mm a")}
                            </TableCell>
                            <TableCell>
                              <Badge variant={patient.enrolled_for_monitorhealth_services ? "default" : "secondary"}>
                                {patient.enrolled_for_monitorhealth_services ? "Yes" : "No"}
                              </Badge>
                            </TableCell>
                            <TableCell>{patient.questionnaire_type_id || 'N/A'}</TableCell>
                            <TableCell>
                              {patient.sms_sent_at 
                                ? formatDate(patient.sms_sent_at, "do MMM yyyy 'at' h:mm a") 
                                : 'Not sent'}
                            </TableCell>
                            <TableCell>
                              <StatusCell status={patient.status} />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                );
              })()}

            </div>

            <div className="flex justify-end gap-3">
              <Dialog open={isBulkAssignOpen} onOpenChange={setIsBulkAssignOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={selectedPatients.length === 0 || importing}
                    className="min-w-[160px]"
                  >
                    Enroll for MHAI (Assign Tests)
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Enroll for MHAI (Assign Tests)</DialogTitle>
                    <div className="text-sm text-muted-foreground mt-2">
                      Assigning tests to {selectedPatients.length} selected patients.
                    </div>
                  </DialogHeader>
                  
                  <div className="py-4 space-y-4">
                    <div className="text-sm font-medium">Select up to 2 tests:</div>
                    <div className="grid grid-cols-1 gap-2">
                       {(() => {
                         const ages = list
                           .filter(p => selectedPatients.includes(p.id))
                           .map(p => calculateAge(p.date_of_birth))
                           .filter(a => a !== null) as number[];

                         const filteredTests = availableTests.filter((test) => {
                           if (ages.length === 0) return true;
                           const min = typeof test.min_age === 'number' && !isNaN(test.min_age) ? test.min_age : 
                                       typeof test.age_min === 'number' && !isNaN(test.age_min) ? test.age_min : 0;
                           const max = typeof test.max_age === 'number' && !isNaN(test.max_age) ? test.max_age : 
                                       typeof test.age_max === 'number' && !isNaN(test.age_max) ? test.age_max : 999;
                           return ages.every(age => age >= min && age <= max);
                         });

                         if (filteredTests.length === 0) {
                           return <div className="text-red-500 text-sm">No common tests available for the selected patients' age range.</div>;
                         }

                         return filteredTests.map((test) => {
                           const isSelected = selectedTests.includes(test.id);
                           const isDisabled = selectedTests.length >= 2 && !isSelected;
                           return (
                             <div
                               key={test.id}
                               className={cn(
                                 "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                                 isSelected ? "bg-primary/5 border-primary" : "bg-white border-gray-200 hover:bg-gray-50",
                                 isDisabled && "opacity-50 cursor-not-allowed"
                               )}
                               onClick={() => { if (!isDisabled) toggleTest(test.id); }}
                             >
                               <Checkbox
                                 checked={isSelected}
                                 disabled={isDisabled}
                                 onCheckedChange={() => { if (!isDisabled) toggleTest(test.id); }}
                                 className="border-gray-300"
                               />
                               <div className="ml-3 flex-1 flex flex-col">
                                 <Label className="text-sm font-medium cursor-pointer">
                                   {test.full_name || test.name || test.id}
                                 </Label>
                                 {test.source_citations && (
                                   <span className="text-[11px] text-muted-foreground mt-0.5 italic">
                                     Citation: {typeof test.source_citations === 'string' ? test.source_citations : JSON.stringify(test.source_citations)}
                                   </span>
                                 )}
                               </div>
                             </div>
                           )
                         });
                       })()}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsBulkAssignOpen(false)}>Cancel</Button>
                    <Button 
                      onClick={handleBulkAssign} 
                      disabled={selectedTests.length === 0 || updating}
                    >
                      {updating ? "Assigning..." : "Assign Tests"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Button
                onClick={handleSendMessage}
                disabled={
                  selectedPatients.length === 0 ||
                  importing ||
                  list.filter(p => selectedPatients.includes(p.id)).every(p => !p.questionnaire_type_id || p.questionnaire_type_id.length === 0)
                }
                title={
                  list.filter(p => selectedPatients.includes(p.id)).every(p => !p.questionnaire_type_id || p.questionnaire_type_id.length === 0)
                    ? "Selected patients have no assessment form assigned. Use 'Enroll for MHAI' first."
                    : undefined
                }
                className="min-w-[160px]"
              >
                {importing ? 'Processing...' : `Send Message (${selectedPatients.length})`}
              </Button>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Patients;
